import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader2, X, ChevronLeft, ChevronRight, RotateCcw, Plus } from 'lucide-react'
import { apiGet, cachedGet, postJson } from '../../lib/api.js'
import { usePersistedState, useEphemeralState } from '../../lib/persist.js'
import { useMe } from '../../lib/me.js'
import { resolveDefaultBranch, pickableBranches } from '../../lib/branch.js'
import { fullName } from '../../lib/employee.js'
import { nowYM, stepMonth, fmtMonthTH } from '../../lib/timetable.js'
import { TIMETABLE_TYPES, shiftTypeTone, relabelBranch } from '../../lib/clockin.js'
import {
  todayISO, typeCounts, codeOf, blankShift, draftFromRow, errText,
  commonWindow, payloadFrom,
} from '../../lib/employeeSchedule.js'
import { doctorsByDay as countDoctors } from '../../lib/employeeMatrix.js'
import { fetchManyEmployeeMonths, clearEmployeeMonthCache } from '../../lib/crossBranch.js'
import { isShiftRow } from '../../lib/doctorSchedule.js'
import EmployeeMatrix from '../../components/schedule/EmployeeMatrix.jsx'
import EmployeeShiftEditor from '../../components/schedule/EmployeeShiftEditor.jsx'
import SearchableSelect from '../../components/SearchableSelect.jsx'
import ConfirmDialog from '../../components/ConfirmDialog.jsx'

const SELECT = 'rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-700 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

// The grid needs the WHOLE 6-week window in memory (a month cell that silently
// shows page 1 of its shifts is worse than no grid), so the fetch sweeps pages.
// Each row carries three server-side aggregate addons, so the sweep is
// sequential — firing 6 of these in parallel would be self-inflicted load — and
// capped, with the cap surfaced rather than hidden.
const PER_PAGE = 200
const MAX_PAGES = 10
export default function EmployeeSchedule() {
  const me = useMe()
  const [branches, setBranches] = useState([])
  const [employees, setEmployees] = useState([])

  const [ym, setYm] = useEphemeralState('sched:emp:ym', () => nowYM())
  // Branch is a required SCOPE, not an optional filter: an all-branch month is
  // thousands of shifts, which pages out and reads as a wall of chips. Empty
  // means "not resolved yet" — never "all".
  const [branchId, setBranchId] = usePersistedState('sched:emp:branch', '')
  const [employeeId, setEmployeeId] = usePersistedState('sched:emp:emp', '')
  const [shiftType, setShiftType] = usePersistedState('sched:emp:type', '')

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [capped, setCapped] = useState(false)
  const reqId = useRef(0)
  const [tick, setTick] = useState(0)   // bumped after a write, to refetch

  // Doctors rostered per day at this branch — context for the headcount, not a
  // rule (the clinic's assistant-to-doctor ratio varies by branch).
  const [doctorRows, setDoctorRows] = useState([])
  const docReq = useRef(0)

  // Cross-branch lookup: employee_id → their rows anywhere this month. Always
  // loaded, never a preference — the count beside each name is a whole-clinic
  // number, and a day someone is working at another branch is not a day they
  // are free here. See lib/crossBranch.js.
  const [foreign, setForeign] = useState(new Map())
  const [foreignLoading, setForeignLoading] = useState(false)

  // A running bulk assign, then its result — the summary outlives the progress.
  const [bulk, setBulk] = useState(null)        // { total, done }
  const [bulkResult, setBulkResult] = useState(null)
  const [confirmBulk, setConfirmBulk] = useState(null)   // { cells, type, onDone }

  const [editor, setEditor] = useState(null)   // { row, draft, backTo }
  // Whose month is open (employee id). Lives here rather than in the matrix so a
  // shift opened FROM that month can hand you back to it when you are done.
  const [monthPersonId, setMonthPersonId] = useState(null)

  useEffect(() => {
    cachedGet('/api/branches')
      .then((r) => setBranches(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
    cachedGet('/api/hr/employees', { per_page: 500, active: 'true' })
      .then((r) => setEmployees(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
  }, [])

  const empById = useMemo(() => {
    const m = new Map()
    for (const e of employees) m.set(Number(e.id), e)
    return m
  }, [employees])

  const branchOptions = useMemo(() => pickableBranches(branches), [branches])

  // First visit (or a cleared choice) opens on the branch this user is actually
  // working today: their employee_timetable row for today → their home branch →
  // สนามเป้า remapped to อารีย์. Resolved server-side, from the session.
  useEffect(() => {
    // A branch remembered from before it left the picker would leave the select
    // showing nothing; clearing it hands the choice back to the resolver.
    if (branchId && branches.length && !branchOptions.some((b) => String(b.id) === String(branchId))) {
      setBranchId('')
      return
    }
    if (branchId || !me) return
    let on = true
    resolveDefaultBranch(me).then((b) => { if (on && b) setBranchId(String(b)) })
    return () => { on = false }
  }, [me, branchId, branchOptions, branches.length]) // eslint-disable-line react-hooks/exhaustive-deps

  // The matrix's columns are the month, so the month is also all we fetch —
  // the calendar's 42-day window existed to fill its leading/trailing cells.
  const range = useMemo(() => {
    const mm = String(ym.month).padStart(2, '0')
    const last = new Date(ym.year, ym.month, 0).getDate()
    return { start_date: `${ym.year}-${mm}-01`, end_date: `${ym.year}-${mm}-${String(last).padStart(2, '0')}` }
  }, [ym])
  const monthRange = range
  const filterKey = JSON.stringify([range, branchId, employeeId, shiftType])

  useEffect(() => {
    if (!branchId) return          // wait for the default to resolve — never fetch all branches
    const id = ++reqId.current
    setLoading(true); setErr(''); setCapped(false)
    ;(async () => {
      // Keyed by row id: a non-unique server sort means a page boundary can
      // repeat a row, and a duplicate chip would read as a duplicate shift.
      const seen = new Map()
      let page = 1
      let hitCap = false
      try {
        for (;;) {
          const r = await apiGet('/api/hr/schedule/employee', {
            ...range,
            employee_id: employeeId || undefined,
            timetable_branch_id: branchId || undefined,
            timetable_type: shiftType || undefined,
            page,
            per_page: PER_PAGE,
          })
          if (id !== reqId.current) return
          for (const it of (Array.isArray(r) ? r : (r?.items || []))) seen.set(Number(it.id), it)
          const pageTotal = Number(r?.pageTotal) || 1
          if (page >= pageTotal) break
          if (page >= MAX_PAGES) { hitCap = true; break }
          page += 1
        }
        if (id !== reqId.current) return
        setRows([...seen.values()])
        setCapped(hitCap)
      } catch (e) {
        if (id === reqId.current) setErr(errText(e, 'โหลดตารางเวรไม่สำเร็จ'))
      } finally {
        if (id === reqId.current) setLoading(false)
      }
    })()
  }, [filterKey, tick]) // eslint-disable-line react-hooks/exhaustive-deps

  // One request per branch-month, shared by the matrix footer and the day panel.
  useEffect(() => {
    if (!branchId) { setDoctorRows([]); return }
    const id = ++docReq.current
    apiGet('/api/hr/schedule/doctor', {
      branch_id: branchId, start_date: monthRange.start_date, end_date: monthRange.end_date,
    })
      .then((r) => {
        if (id !== docReq.current) return
        setDoctorRows((Array.isArray(r) ? r : (r?.items || [])).filter(isShiftRow))
      })
      .catch(() => { if (id === docReq.current) setDoctorRows([]) })
  }, [branchId, monthRange.start_date, monthRange.end_date, tick])

  // A different month or branch invalidates what was loaded for the last one.
  useEffect(() => { setForeign(new Map()) }, [monthRange.start_date, branchId])

  const monthRows = useMemo(
    () => rows.filter((r) => String(r.date || '').slice(0, 10).startsWith(`${ym.year}-${String(ym.month).padStart(2, '0')}`)),
    [rows, ym],
  )
  const counts = useMemo(() => typeCounts(monthRows), [monthRows])
  const docsByDay = useMemo(() => countDoctors(doctorRows), [doctorRows])

  // Everyone the matrix can show a row for: rostered here this month, or based
  // here. Sorted + joined so the effect below re-runs on a real change of set,
  // not on every refetch that rebuilds the same array.
  const foreignIds = useMemo(() => {
    const s = new Set()
    for (const r of monthRows) { const id = Number(r.employee_id); if (id) s.add(id) }
    for (const e of employees) if (Number(e.branch_id) === Number(branchId)) s.add(Number(e.id))
    return [...s].sort((a, b) => a - b)
  }, [monthRows, employees, branchId])
  const foreignIdsKey = foreignIds.join(',')

  // One request per person, cached in lib/crossBranch.js, so this re-runs only
  // when the roster, the month or the branch actually changes — or after a write.
  useEffect(() => {
    if (!branchId || !foreignIds.length) return
    let on = true
    setForeignLoading(true)
    fetchManyEmployeeMonths(foreignIds, monthRange)
      .then((m) => { if (on) setForeign(m) })
      .finally(() => { if (on) setForeignLoading(false) })
    return () => { on = false }
  }, [branchId, foreignIdsKey, monthRange.start_date, monthRange.end_date, tick]) // eslint-disable-line react-hooks/exhaustive-deps

  /* The matrix asks for exactly the people it needs — whoever is in the current
   * selection — so a day already worked at another branch is known before the
   * assign rather than discovered as a failure after it. Cached per employee
   * per month, so this is one small request the first time each person is
   * picked and nothing thereafter. */
  async function ensureForeign(ids) {
    const want = (ids || []).filter((id) => id && !foreign.has(id))
    if (!want.length) return
    const got = await fetchManyEmployeeMonths(want, monthRange)
    if (!got.size) return
    setForeign((prev) => new Map([...prev, ...got]))
  }

  /**
   * Give every selected (person, day) the same shift type.
   *
   * One POST per row rather than a bulk endpoint: Xano's create is what enforces
   * one-row-per-(employee, date) — its precondition is skipped whenever an id is
   * sent — so going through it one row at a time is what keeps a bulk assign
   * from being the one path that can mint duplicates. The failures are collected
   * and named instead of aborting the run: a day that was taken while the
   * selection sat on screen should not cost the other twenty-five.
   */
  const BULK_CONFIRM_OVER = 8

  async function bulkAssign(cells, type, onDone) {
    if (!cells?.length || !branchId || bulk) return
    // A misclick on a type chip with a month selected is 26 rows to undo by
    // hand; a week for one person is the normal case and stays one click.
    if (cells.length > BULK_CONFIRM_OVER && !confirmBulk) {
      setConfirmBulk({ cells, type, onDone })
      return
    }
    setConfirmBulk(null)
    const w = commonWindow(rows, type, branchId)
    const failed = []
    let ok = 0
    setBulkResult(null)
    setBulk({ total: cells.length, done: 0 })
    for (const c of cells) {
      try {
        await postJson('/api/hr/schedule/employee', payloadFrom({
          id: 0,
          employee_id: c.employeeId,
          branch_id: Number(branchId),
          type,
          date: c.date,
          start: w.start,
          end: w.end,
          travel_fee: false,
          travel_fee_amount: 0,
          remark: '',
        }))
        ok += 1
      } catch (e) {
        failed.push({ ...c, code: codeOf({ employee_id: c.employeeId }, empById), msg: errText(e) })
      }
      setBulk((b) => (b ? { ...b, done: b.done + 1 } : b))
    }
    setBulk(null)
    setBulkResult({ type, ok, failed })
    clearEmployeeMonthCache()
    onDone?.()
    setTick((t) => t + 1)
  }

  const empOptions = useMemo(() => ([
    { value: '', label: 'ทุกคน' },
    ...employees.map((e) => ({
      value: String(e.id),
      // Code alone — it is the roster key and a full name after it pushes the
      // useful part off the end of the control. The name lives in `hint`, which
      // the picker searches on but does not render.
      label: e.code || fullName(e),
      hint: [fullName(e), e.nickname, e.name_eng].filter(Boolean).join(' · '),
    })),
  ]), [employees])

  const today = todayISO()
  // A new shift lands on the selected day, else today when today is inside the
  // month on screen, else that month's 1st — never on a day the user cannot see.
  const monthPrefix = `${ym.year}-${String(ym.month).padStart(2, '0')}`
  const defaultNewDate = today.startsWith(monthPrefix) ? today : `${monthPrefix}-01`
  // The branch is not part of this — it is the scope and cannot be cleared.
  const hasFilter = employeeId || shiftType
  function openCreate(date, employeeId = 0, type, backTo = null) {
    if (backTo) setMonthPersonId(null)
    const draft = blankShift({
      date,
      branchId: branchId || me?.branch_id || '',
      rows,
      ...(type ? { type } : {}),
    })
    // From a matrix cell the person and their usual type are already decided —
    // the drawer opens on the remaining question (the window) instead of asking
    // again for what the click already said.
    setEditor({ row: null, draft: { ...draft, employee_id: Number(employeeId) || 0 }, backTo })
  }
  function openEdit(row, backTo = null) {
    if (backTo) setMonthPersonId(null)
    setEditor({ row, draft: draftFromRow(row), backTo })
  }

  // Close the shift and reopen the month it was opened from. Used by the back
  // button and by a successful save, because finishing one day of someone's
  // month almost always means looking at the next one.
  function backToMonth() {
    const id = editor?.backTo
    setEditor(null)
    if (id) setMonthPersonId(id)
  }
  function afterWrite() {
    const back = editor?.backTo
    // A saved shift can change two people's months at once (one freed, one
    // taken), and a stale "free" is the one answer that lookup must never give.
    clearEmployeeMonthCache()
    setForeign(new Map())   // the cross-branch effect refetches on `tick`
    setEditor(null)
    if (back) setMonthPersonId(back)
    setTick((t) => t + 1)
  }

  return (
    <div className="max-w-[1280px]">
      <p className="text-[13.5px] text-slate-500 mb-5">
        ตารางเวรพนักงานรายเดือน (แยกตามสาขา) — คลิกช่องเพื่อเพิ่มหรือแก้ไขเวร · Shift + คลิก หรือลากคลุม เพื่อเลือกหลายวัน
      </p>

      {/* month */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button onClick={() => setYm(stepMonth(ym, -1))}
          className="p-2 rounded-lg text-slate-500 ring-1 ring-slate-200 bg-white hover:bg-slate-50" title="เดือนก่อนหน้า">
          <ChevronLeft size={15} />
        </button>
        <div className="min-w-[150px] text-center text-[15px] font-semibold text-slate-900 tabular-nums">
          {fmtMonthTH(ym.year, ym.month)}
        </div>
        <button onClick={() => setYm(stepMonth(ym, 1))}
          className="p-2 rounded-lg text-slate-500 ring-1 ring-slate-200 bg-white hover:bg-slate-50" title="เดือนถัดไป">
          <ChevronRight size={15} />
        </button>
        <button onClick={() => setYm(nowYM())}
          className="inline-flex items-center gap-1.5 px-2.5 py-2 rounded-lg text-[13px] text-slate-500 hover:bg-slate-100">
          <RotateCcw size={14} /> เดือนนี้
        </button>
        <button onClick={() => openCreate(defaultNewDate)}
          className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13.5px] font-semibold text-white bg-brand-600 hover:bg-brand-700">
          <Plus size={15} /> เพิ่มเวร
        </button>
      </div>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2.5 mb-4">
        <select value={branchId} onChange={(e) => setBranchId(e.target.value)}
          className={`${SELECT} font-semibold text-slate-900`}>
          {!branchId && <option value="">เลือกสาขา…</option>}
          {branchOptions.map((b) => <option key={b.id} value={b.id}>{relabelBranch(b.name)}</option>)}
        </select>
        <SearchableSelect
          options={empOptions} value={employeeId} onChange={setEmployeeId}
          placeholder="ทุกคน" searchPlaceholder="พิมพ์รหัสพนักงาน / ชื่อ…" className="min-w-[240px]"
        />
        <select value={shiftType} onChange={(e) => setShiftType(e.target.value)} className={SELECT}>
          <option value="">ทุกประเภทเวร</option>
          {TIMETABLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {hasFilter && (
          <button onClick={() => { setEmployeeId(''); setShiftType('') }}
            className="flex items-center gap-1 px-2.5 py-2 rounded-lg text-[13px] text-slate-500 hover:bg-slate-100">
            <X size={14} /> ล้าง
          </button>
        )}
        <span className="ml-auto text-[12.5px] text-slate-400 tabular-nums">
          {loading ? 'กำลังโหลด…' : `${monthRows.length.toLocaleString()} เวรในเดือนนี้`}
        </span>
      </div>

      {/* legend — doubles as this month's per-type count */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        {TIMETABLE_TYPES.map((t) => {
          const n = counts.get(t) || 0
          return (
            <span key={t}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-medium ring-1 ${n ? shiftTypeTone(t) : 'bg-white text-slate-300 ring-slate-200'}`}>
              {t}<span className="tabular-nums opacity-70">{n}</span>
            </span>
          )
        })}
      </div>

      {err && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700 mb-3">{err}</div>
      )}
      {capped && !err && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] text-amber-800 mb-3">
          เดือนนี้มีเวรมากเกินกว่าที่แสดงได้ทั้งหมด — กรุณาเลือกพนักงานหรือประเภทเวรเพื่อดูข้อมูลครบถ้วน
        </div>
      )}

      {bulk && (
        <div className="mb-3 flex items-center gap-2 rounded-2xl border border-brand-200 bg-brand-50 px-4 py-2.5 text-[13px] text-brand-800">
          <Loader2 size={15} className="animate-spin shrink-0" />
          <span className="tabular-nums">กำลังเพิ่มเวร {bulk.done}/{bulk.total} …</span>
        </div>
      )}
      {bulkResult && !bulk && (
        <div className={`mb-3 flex items-start gap-2 rounded-2xl border px-4 py-2.5 text-[13px] ${
          bulkResult.failed.length ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>
          <div className="flex-1 min-w-0">
            <span className="tabular-nums">เพิ่ม {bulkResult.type} แล้ว {bulkResult.ok} เวร</span>
            {bulkResult.failed.length > 0 && (
              <>
                <span className="tabular-nums"> · ไม่สำเร็จ {bulkResult.failed.length}</span>
                <ul className="mt-1 space-y-0.5 text-[12px]">
                  {bulkResult.failed.slice(0, 4).map((f) => (
                    <li key={`${f.employeeId}|${f.date}`} className="truncate">{f.code} · {f.date} — {f.msg}</li>
                  ))}
                  {bulkResult.failed.length > 4 && <li>และอีก {bulkResult.failed.length - 4} รายการ</li>}
                </ul>
              </>
            )}
          </div>
          <button onClick={() => setBulkResult(null)} className="shrink-0 opacity-60 hover:opacity-100"><X size={15} /></button>
        </div>
      )}

        <div className="relative">
          {loading && (
            <div className="absolute inset-0 z-10 bg-white/70 flex items-center justify-center text-[13px] text-slate-500 rounded-2xl">
              <Loader2 size={18} className="animate-spin mr-2" /> กำลังโหลดตารางเวร…
            </div>
          )}
          <EmployeeMatrix
            ym={ym}
            rows={monthRows}
            employees={employees}
            empById={empById}
            branchId={branchId}
            branches={branches}
            doctorsByDay={docsByDay}
            foreign={foreign}
            foreignLoading={foreignLoading}
            onNeedForeign={ensureForeign}
            openPersonId={monthPersonId}
            onOpenPerson={setMonthPersonId}
            onCreate={openCreate}
            onEdit={openEdit}
            onBulkAssign={bulkAssign}
            busy={!!bulk}
            today={today}
          />
        </div>

      {editor && (
        <EmployeeShiftEditor
          key={editor.row?.id || `new:${editor.draft.date}`}
          row={editor.row}
          draft={editor.draft}
          rows={rows}
          range={range}
          branches={branches}
          employees={employees}
          onClose={() => setEditor(null)}
          onBack={editor.backTo ? backToMonth : undefined}
          backLabel={editor.backTo ? codeOf({ employee_id: editor.backTo }, empById) : ''}
          onSaved={afterWrite}
          onDeleted={afterWrite}
        />
      )}

      {confirmBulk && (
        <ConfirmDialog
          title={`กำหนด ${confirmBulk.type} ${confirmBulk.cells.length} เวร`}
          message={`ระบบจะเพิ่มเวร ${confirmBulk.type} ให้ ${new Set(confirmBulk.cells.map((c) => c.employeeId)).size} คน รวม ${confirmBulk.cells.length} วัน ที่สาขานี้ · วันที่มีเวรอยู่แล้วจะถูกข้ามและแจ้งให้ทราบ`}
          confirmLabel="เพิ่มเวร"
          onConfirm={() => bulkAssign(confirmBulk.cells, confirmBulk.type, confirmBulk.onDone)}
          onCancel={() => setConfirmBulk(null)}
        />
      )}

    </div>
  )
}
